import { useEffect, useRef, useState, useCallback } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function useAutosave<T>(
  data: T,
  saveFn: (data: T) => Promise<void>,
  delay: number = 1000
) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const lastSavedData = useRef<string>(JSON.stringify(data));

  const debouncedSave = useCallback(async (currentData: T) => {
    const dataStr = JSON.stringify(currentData);
    if (dataStr === lastSavedData.current) {
      setStatus('idle');
      return;
    }

    setStatus('saving');
    try {
      await saveFn(currentData);
      lastSavedData.current = dataStr;
      setStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      setStatus('error');
    }
  }, [saveFn]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSavedData.current = JSON.stringify(data);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Check if data actually changed before starting the timer
    const dataStr = JSON.stringify(data);
    if (dataStr === lastSavedData.current) {
      if (status !== 'saved' && status !== 'saving') {
        setStatus('idle');
      }
      return;
    }

    timerRef.current = setTimeout(() => {
      debouncedSave(data);
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [data, delay, debouncedSave]);

  const setManualData = useCallback((newData: T) => {
    lastSavedData.current = JSON.stringify(newData);
    setStatus('idle');
  }, []);

  return { status, setStatus, setManualData };
}
